/**
 * A free-text field that also offers a filterable list of values to pick from.
 *
 * For a value somebody usually types but often repeats — an occasion, a
 * branch name — where the options are suggestions and never the only choices.
 * That is what separates it from `Select` (the options are the only choices)
 * and `RecordSelect` (the value is a record id, not the text).
 *
 * The design decisions worth knowing:
 *
 * * **The ARIA combobox pattern, with focus left in the input.** The listbox is
 *   tied to the input by `aria-controls` and the highlighted option by
 *   `aria-activedescendant`, so typing and arrowing never move focus.
 * * **The list is rendered in place, not through `Popover`.** `Popover` is a
 *   `dialog` in a portal that dismisses on any pointer-down outside its panel —
 *   and the input a combobox types into is outside that panel, so every click
 *   back into the field would close the list it is filtering.
 * * **Options fill the input on `mousedown`, with the default prevented.** A
 *   click fires after the input's blur, and the blur is what closes the list;
 *   choosing on pointer-down keeps focus where it is and the choice lands.
 */

import { useId, useRef, useState, type InputHTMLAttributes, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';

import { cn } from '../../lib/cn';
import { readValue, useReadOnly } from '../../lib/read-only';
import { Field } from './input';
import { ScrollArea } from './scroll-area';

export interface ComboboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'value' | 'onChange'> {
  label?: string;
  hint?: string;
  error?: string;
  value: string;
  onChange: (value: string) => void;
  /** Suggestions, in the order they are offered. Duplicates are shown once. */
  options: readonly string[];
  /**
   * Offers a remove button on each option. Absent, the options are read-only —
   * the button is not rendered rather than rendered disabled.
   */
  onRemoveOption?: (option: string) => void;
  /** Said inside an open list that has nothing matching the text typed. */
  emptyLabel?: string;
  containerClassName?: string;
}

/** The options containing `term`, ignoring case, each once. An empty term matches all. */
export function filterOptions(options: readonly string[], term: string): string[] {
  const needle = term.trim().toLocaleLowerCase();
  const unique = [...new Set(options)];
  return needle ? unique.filter((option) => option.toLocaleLowerCase().includes(needle)) : unique;
}

export function Combobox({
  label,
  hint,
  error,
  value,
  onChange,
  options,
  onRemoveOption,
  emptyLabel = 'Nothing saved matches that.',
  className,
  containerClassName,
  id,
  required,
  disabled,
  onBlur,
  onFocus,
  onKeyDown,
  ...rest
}: ComboboxProps) {
  const reading = useReadOnly();
  const generated = useId();
  const inputId = id ?? generated;
  const listId = `${inputId}-list`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const shown = filterOptions(options, value);
  // Nothing to offer at all is no list, rather than an empty one: a field with
  // no saved values yet should look like a plain input.
  const expanded = open && options.length > 0 && !disabled;

  const choose = (option: string) => {
    onChange(option);
    setOpen(false);
    setActive(-1);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!expanded) {
        setOpen(true);
        setActive(event.key === 'ArrowDown' ? 0 : shown.length - 1);
        return;
      }
      if (shown.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + shown.length) % shown.length);
    } else if (event.key === 'Enter') {
      const option = expanded ? shown[active] : undefined;
      // Only a highlighted option is taken; otherwise Enter keeps its usual
      // meaning for the surrounding form.
      if (option !== undefined) {
        event.preventDefault();
        choose(option);
      }
    } else if (event.key === 'Escape') {
      if (expanded) {
        // Stopped here so a combobox inside a dialog closes its list first,
        // and the dialog only on a second Escape.
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        setActive(-1);
      }
    }
  };

  const activeOption = expanded && active >= 0 && active < shown.length ? active : -1;

  if (reading) {
    // Read, not edited (lib/read-only.tsx).
    return (
      <Field label={label} hint={hint} className={containerClassName}>
        <p className="flex min-h-8 items-center text-sm text-body">{readValue(value)}</p>
      </Field>
    );
  }
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      htmlFor={inputId}
      className={containerClassName}
    >
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          required={required}
          disabled={disabled}
          value={value}
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-activedescendant={activeOption >= 0 ? `${listId}-${activeOption}` : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-message` : undefined}
          className={cn(
            'h-9.5 w-full rounded-control border bg-surface px-3 text-sm text-body',
            'placeholder:text-faint transition-colors',
            'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-faint',
            'aria-invalid:border-danger aria-invalid:ring-1 aria-invalid:ring-danger/30',
            'border-line focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent/30',
            className,
          )}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={(event) => {
            onFocus?.(event);
            setOpen(true);
          }}
          onClick={() => setOpen(true)}
          onBlur={(event) => {
            onBlur?.(event);
            setOpen(false);
            setActive(-1);
          }}
          onKeyDown={handleKeyDown}
          {...rest}
        />

        {expanded ? (
          <ScrollArea className="absolute inset-x-0 top-full z-dropdown mt-1 max-h-56 rounded-card border border-line bg-surface-popup shadow-dropdown"><ul
 id={listId}
 role="listbox"
 aria-label={label ?? 'Suggestions'}
 className="py-1"
>
            {shown.length === 0 ? (
              <li className="px-3 py-2 text-xs text-faint">{emptyLabel}</li>
            ) : (
              shown.map((option, index) => (
                <li
                  key={option}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === activeOption}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    choose(option);
                  }}
                  onMouseEnter={() => setActive(index)}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-body',
                    index === activeOption && 'bg-surface-2',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{option}</span>
                  {onRemoveOption ? (
                    <button
                      type="button"
                      // Not in the tab order: focus stays in the input, as the
                      // pattern requires. The pointer is the way to remove.
                      tabIndex={-1}
                      aria-label={`Remove ${option}`}
                      className="shrink-0 cursor-pointer rounded p-0.5 text-faint hover:bg-surface hover:text-danger"
                      onMouseDown={(event) => {
                        // Before the option's own handler, which would fill
                        // the input with the value being removed.
                        event.preventDefault();
                        event.stopPropagation();
                        onRemoveOption(option);
                      }}
                    >
                      <X className="size-3.5" aria-hidden />
                    </button>
                  ) : null}
                </li>
              ))
            )}
          </ul></ScrollArea>
        ) : null}
      </div>
    </Field>
  );
}
